import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import { useApplication } from 'src/context/ApplicationContext';
import { KOSTEN_POSITION, PLATZHALTER_TEXT, UEBERARBEITUNGS_HINWEIS_TITLE } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, NonNegativeDecimalValidation, NonRequired } from 'src/lib/validation/ValidationTypes';
import {
	checkIfAnyPositionNachPositionArtHasComment,
	checkIfSpecificKostenPositionHasComment,
} from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import { DisplayEuro } from 'src/utils/formats';
import { checkReadOnlyInUeberarbeitung } from 'src/utils/helper';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';

// import Schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = Schema.attributes;
const KP_P = KostenSchema.attributes;

const FinanzierungsplanMessefoerderung: FC<{ isKostenEditable: boolean }> = ({ isKostenEditable }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();

	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, isKostenEditable);

	return (
		<>
			<ApplicationHeading title='Finanzierungsplan' description={PLATZHALTER_TEXT} />

			<CreateTable
				formStruc={formStrucKosten(application, updatedReadOnly)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
				columnsLabel={columnsLabel(updatedReadOnly, application)}
			/>

			<CreateForm
				formStruc={formStrucBeantragteFoerderung()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
				columnsLabel={columnsLabel(updatedReadOnly, application)}
			/>
		</>
	);
};
export default FinanzierungsplanMessefoerderung;

const columnsLabel = (readOnly: boolean, application: any) => {
	let labels = ['Art der Ausgabe', 'Betrag (€)', 'Erläuterung'];

	if (checkIfAnyPositionNachPositionArtHasComment(readOnly, application, KOSTEN_POSITION.Ausgabe)) {
		labels.push('Erledigt', UEBERARBEITUNGS_HINWEIS_TITLE);
	}

	return labels;
};

const formStrucKosten = (application: any, readOnly: boolean) => {
	let struc: FormStrucItem[] = [];

	const allKostenPositions = get(application, P.Foerderantrag_Kostenpositions.path, []);
	const allEinnahmen = allKostenPositions.filter((ele: any) => get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme);
	const summeDerFinanzierung = allEinnahmen.reduce(
		(acc: any, ele: any) => acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0')),
		0,
	);

	for (const [index, eachKosten] of allKostenPositions.entries()) {
		if (get(eachKosten, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme) {
			let tempStruc: FormStrucItem = {
				fields: [
					{
						label: get(eachKosten, KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Bezeichnung.path),
						tooltip: get(eachKosten, KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Erlaeuterung_Fuer_Den_Antragsteller.path),
						path: 't.t',
						type: '',
						validation: [{ validateFunction: NonRequired }],
					},
					{ ...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme, format: 'currency' },
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Erlaeuterungen,
						minRows: 2,
						validation: [{ validateFunction: NonRequired }],
					},
				],
			};

			// Adding extra fields based on condition
			if (checkIfSpecificKostenPositionHasComment(readOnly, eachKosten)) {
				tempStruc.fields.push(
					{
						...P.Foerderantrag_Kostenpositions.schema.attributes.Pruefung_Erledigt,
						validation: [{ validateFunction: ForceTrue }],
					},
					{
						label: get(eachKosten, KP_P.Pruefung_Hinweis_Fuer_Antragsteller.path, 'Bitte überarbeiten Sie diese Inhalte.'),
						path: 't.t',
						type: '',
						validation: [{ validateFunction: NonRequired }],
					},
				);
			}

			// Push the structured object to struc
			struc.push(...mapKosten([tempStruc], index));
		}
	}
	struc.push({
		fields: [
			{
				label: 'Gesamtsumme',
				path: 't.t',
				type: '',
				validation: [{ validateFunction: NonRequired }],
			},
			{
				// label: DisplayEuro(get(application, P.Allgemein_Beantragte_Zuwendung.path, '0')),
				label: DisplayEuro(!isNaN(summeDerFinanzierung) ? summeDerFinanzierung : 0),
				path: 't.t',
				type: '',
				validation: [{ validateFunction: NonRequired }],
			},
		],
	});
	return struc; // Return the constructed array
};
const formStrucBeantragteFoerderung = () => {
	/**
	 * Erstmal nur auskommentieren bis Abnahme. Da HA nochmal die Formell haben wollen kann.
	 * Steht im Ticket HA-1411, dass der Antragsteller die beantragte Förderung selber eintragen muss.
	 * Wenn die Förderung wieder berechnet werden soll, muss das Formell auch angepassst werden und diese formStruc auch der concat unten rausgenommen werden.
	 */
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Allgemein_Beantragte_Zuwendung,
					format: 'currency',
					validation: [{ validateFunction: NonNegativeDecimalValidation }] /*, disabled:true*/,
				},
			],
		},
	];
	return struc;
};

const mapKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Foerderantrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Foerderantrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};

export const getFormStrucFinanzierungsplanMessefoerderung = (application: any, readOnly: boolean) => {
	return concat(formStrucKosten(application, readOnly), formStrucBeantragteFoerderung());
};
